'use client';

import React from 'react';
import { Exam } from '@/lib/services/examService';
import { Calendar, Globe, ChevronRight, Users, Building2 } from 'lucide-react';

interface ExamCardProps {
  exam: Exam;
  isSelected: boolean;
  onSelect: () => void;
}

export default function ExamCard({ exam, isSelected, onSelect }: ExamCardProps) {
  const streamBadgeClass =
    exam.stream[0] === 'Engineering' ? 'stream-badge-engineering' :
    exam.stream[0] === 'Medical' ? 'stream-badge-medical' :
    exam.stream[0] === 'Management' ? 'stream-badge-management' :
    exam.stream[0] === 'Law' ? 'stream-badge-law' :
    exam.stream[0] === 'Arts'? 'stream-badge-arts' : 'stream-badge-commerce';

  const modeColor =
    exam.mode === 'Online' ? 'text-green-600 bg-green-50' :
    exam.mode === 'Offline'? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';

  const levelColor =
    exam.level === 'National' ? 'text-blue-700 bg-blue-50' : 'text-purple-700 bg-purple-50';

  const formatApplicants = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return n.toString();
  };

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left card-base p-4 transition-all duration-150 hover:shadow-card-hover hover:border-primary/40 ${
        isSelected ? 'ring-2 ring-primary border-primary bg-accent/30' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${streamBadgeClass}`}>
              {exam.stream[0]}
            </span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor}`}>
              {exam.level}
            </span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${modeColor}`}>
              {exam.mode}
            </span>
          </div>
          <h3 className="font-bold text-base text-foreground leading-tight">{exam.shortName}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{exam.conductingBody}</p>
        </div>
        <ChevronRight size={16} className={`text-muted-foreground shrink-0 mt-1 transition-transform ${isSelected ? 'text-primary rotate-90' : ''}`} />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Calendar size={12} className="shrink-0" />
          <span className="truncate">{exam.examDate}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Users size={12} className="shrink-0" />
          <span>{formatApplicants(exam.totalApplicants)} applicants</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Building2 size={12} className="shrink-0" />
          <span>{exam.totalSeats.toLocaleString()} seats</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Globe size={12} className="shrink-0" />
          <span className="truncate">{exam.officialWebsite}</span>
        </div>
      </div>

      {/* Registration deadline alert */}
      <div className="mt-3 pt-2 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Reg. Deadline</span>
        <span className="text-xs font-semibold text-foreground">{exam.registrationEnd}</span>
      </div>
    </button>
  );
}